import type {
  CreateReportBody,
  CreateReportResponse,
  DeleteReportResponse,
  GetReportsAvailableResponseRow,
  GetReportsParams,
  GetReportsResponse,
} from '../types/reportsService.types';
import type { GetReportRow } from '../types/reports.types';

// ===================== Reports List Mocks =====================

export const mockReportRows: GetReportRow[] = [
  {
    id: 1,
    name: 'Relatório Frequência - Janeiro',
    status: 'completed',
    report_type: 'MobilePositionFrequencyReport',
    created_at: '2026-01-15T10:30:00Z',
    params_from: '2026-01-01T00:00:00Z',
    params_until: '2026-01-31T23:59:59Z',
    devices_count: 150,
    download_url: 'https://mock-storage.example.com/reports/report-1.csv',
  },
  {
    id: 2,
    name: 'Relatório Endereços por Dispositivo - Fevereiro',
    status: 'running',
    report_type: 'DeviceKeyAddressReport',
    created_at: '2026-02-10T14:00:00Z',
    params_from: '2026-02-01T00:00:00Z',
    params_until: '2026-02-28T23:59:59Z',
    devices_count: 80,
    download_url: '',
  },
  {
    id: 3,
    name: 'Relatório Registro de Dispositivos - Fevereiro',
    status: 'pending',
    report_type: 'DeviceKeyRegistryReport',
    created_at: '2026-02-12T09:15:00Z',
    params_from: '2026-02-01T00:00:00Z',
    params_until: '2026-02-15T23:59:59Z',
    devices_count: 45,
    download_url: '',
  },
  {
    id: 4,
    name: 'Relatório Frequência - Dezembro',
    status: 'completed',
    report_type: 'MobilePositionFrequencyReport',
    created_at: '2025-12-20T16:45:00Z',
    params_from: '2025-12-01T00:00:00Z',
    params_until: '2025-12-31T23:59:59Z',
    devices_count: 200,
    download_url: 'https://mock-storage.example.com/reports/report-4.csv',
  },
  {
    id: 5,
    name: 'Relatório Endereços por Dispositivo - Erro',
    status: 'errored',
    report_type: 'DeviceKeyAddressReport',
    created_at: '2026-02-05T08:00:00Z',
    params_from: '2026-01-15T00:00:00Z',
    params_until: '2026-02-05T23:59:59Z',
    devices_count: 10,
    download_url: '',
  },
  {
    id: 6,
    name: 'Relatório Frequência - Novembro',
    status: 'completed',
    report_type: 'MobilePositionFrequencyReport',
    created_at: '2025-11-25T11:00:00Z',
    params_from: '2025-11-01T00:00:00Z',
    params_until: '2025-11-30T23:59:59Z',
    devices_count: 175,
    download_url: 'https://mock-storage.example.com/reports/report-6.csv',
  },
];

// ===================== Available Reports Mocks =====================

export const mockReportsAvailable: GetReportsAvailableResponseRow[] = [
  {
    type: 'MobilePositionFrequencyReport',
    name: 'Mobile Position Frequency Report',
    frontEndArgs: ['from', 'until', 'group_ids', 'device_ids'],
    task_type: 'MobilePositionFrequencyReport',
  },
  {
    type: 'DeviceKeyRegistryReport',
    name: 'Registro de Dispositivos',
    frontEndArgs: [],
    task_type: 'DeviceKeyRegistryReport',
  },
  {
    type: 'DeviceKeyTagsReport',
    name: 'Listagem de Tags por Dispositivo',
    frontEndArgs: [],
    task_type: 'DeviceKeyTagsReport',
  },
  {
    type: 'DeviceKeyAddressReport',
    name: 'Device Key Address',
    frontEndArgs: [],
    task_type: 'DeviceKeyAddressReport',
  },
  {
    type: 'RemoteIdAddressReport',
    name: 'Remote ID Address',
    frontEndArgs: ['remote_id'],
    task_type: 'RemoteIdAddressReport',
  },
  {
    type: 'DeviceKeyEventLogReport',
    name: 'Device Key Event Log',
    frontEndArgs: ['from', 'until'],
    task_type: 'DeviceKeyEventLogReport',
  },
];

// ===================== Devices Activated Mocks =====================

interface MockDeviceResult {
  device_id: number;
  channels: number;
  activated_at: Date;
  remote_id: string[];
  name: string[];
  channels_activated: boolean[];
  display_address: string;
  is_pre_activation: boolean;
}

interface MockDevicesActivatedResponse {
  results: MockDeviceResult[];
  too_many_results: boolean;
}

interface MockDevicesActivatedCountResponse {
  device_ids: number[];
  count: number;
}

const mockDeviceResults: MockDeviceResult[] = [
  {
    device_id: 1001,
    channels: 2,
    activated_at: new Date('2026-01-10T08:00:00Z'),
    remote_id: ['RM-001', 'RM-002'],
    name: ['Sensor Alpha', 'Sensor Beta'],
    channels_activated: [true, true],
    display_address: 'Rua Augusta, 1500 - São Paulo, SP',
    is_pre_activation: false,
  },
  {
    device_id: 1002,
    channels: 1,
    activated_at: new Date('2026-01-15T10:30:00Z'),
    remote_id: ['RM-003'],
    name: ['Sensor Gamma'],
    channels_activated: [true],
    display_address: 'Av. Paulista, 900 - São Paulo, SP',
    is_pre_activation: false,
  },
  {
    device_id: 1003,
    channels: 3,
    activated_at: new Date('2026-02-01T14:00:00Z'),
    remote_id: ['RM-004', 'RM-005', 'RM-006'],
    name: ['Sensor Delta', 'Sensor Epsilon', 'Sensor Zeta'],
    channels_activated: [true, true, false],
    display_address: 'Rua Oscar Freire, 200 - São Paulo, SP',
    is_pre_activation: false,
  },
  {
    device_id: 1004,
    channels: 1,
    activated_at: new Date('2026-02-05T09:00:00Z'),
    remote_id: ['RM-007'],
    name: ['Sensor Eta'],
    channels_activated: [false],
    display_address: 'Av. Brigadeiro Faria Lima, 3000 - São Paulo, SP',
    is_pre_activation: true,
  },
];

export const mockDevicesActivatedResponse: MockDevicesActivatedResponse = {
  results: mockDeviceResults,
  too_many_results: false,
};

export const mockDevicesActivatedCountResponse: MockDevicesActivatedCountResponse = {
  device_ids: mockDeviceResults.map(d => d.device_id),
  count: mockDeviceResults.length,
};

// ===================== Create Report Mock (stateful) =====================

let mockReportIdCounter = 100;

export const mockCreateReport = (body: CreateReportBody): CreateReportResponse => {
  mockReportIdCounter += 1;

  const newReport: GetReportRow = {
    id: mockReportIdCounter,
    name: body.name,
    status: 'pending',
    report_type: body.type,
    created_at: new Date().toISOString(),
    params_from: body.params?.from ?? '',
    params_until: body.params?.until ?? '',
    devices_count: 0,
    download_url: '',
  };

  mockReportRows.push(newReport);

  return { id: String(mockReportIdCounter) };
};

// ===================== Delete Report Mock (stateful) =====================

export const mockDeleteReport = (id: number): DeleteReportResponse => {
  const index = mockReportRows.findIndex(r => r.id === id);

  if (index !== -1) {
    mockReportRows.splice(index, 1);
  }

  return { code: 204 };
};

// ===================== Get Reports Mock (with search filter) =====================

export const mockGetReports = ({
  name,
  type,
}: Omit<GetReportsParams, 'signal'>): GetReportsResponse => {
  let rows = [...mockReportRows];

  if (name) {
    rows = rows.filter(r => r.name.toLowerCase().includes(name.toLowerCase()));
  }

  if (type) {
    rows = rows.filter(r => r.report_type === type);
  }

  return { rows, has_more: false };
};
